import { Injectable, NgZone, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import type { LocaleId } from '../../core/i18n/locale-id';
import {
  CdnWebChatConfiguratorLoader,
  WebChatConfiguratorConstructor,
  WebChatConfiguratorInstance,
  WebChatConfiguratorLoader,
} from './web-chat-configurator.loader';

const OMILIA_SESSION_ACTIVE_KEY = 'omilia_session_active';
const HIDE_BUBBLE_STYLE_ID = 'puro-hide-omilia-bubble';
const PANEL_OPEN_TIMEOUT_MS = 20_000;
const PANEL_OPEN_RETRY_MS = 400;
const SDK_READY_TIMEOUT_MS = 15_000;
const END_CHAT_MARK_CLOSED_DELAY_MS = 300;
const END_CHAT_BUTTON_PATTERN = /end chat|terminer/i;

const HEADER_LOGO = 'https://74n6.mjt.lu/img2/74n6/26dbc4c2-203f-4bf4-9ee9-bef3df4ca4fb/content';
const BUBBLE_URL = 'https://74n6.mjt.lu/img2/74n6/cc6a7e9e-c2cd-4686-8c3c-236a616fe3bb/content';

/**
 * Detects mobile agents via the `Mobile` token (drives popup vs inline displayMode).
 */
export function isMobileUserAgent(userAgent: string): boolean {
  return userAgent.includes('Mobile');
}

/**
 * Opens Purolator's Omilia chat via the same native WebChatConfigurator used on purolator.com.
 * Our FAB is the only launcher; the widget owns chrome, minimize/close, and close confirmation.
 */
@Injectable({ providedIn: 'root' })
export class ChatBotService {
  private readonly zone = inject(NgZone);
  private readonly loader =
    inject(WebChatConfiguratorLoader, { optional: true }) ?? new CdnWebChatConfiguratorLoader();

  private readonly openSignal = signal(false);
  private throttleUntil = 0;
  private configurator: WebChatConfiguratorInstance | null = null;
  private initPromise: Promise<WebChatConfiguratorInstance> | null = null;
  private openGeneration = 0;
  private activeLocale: LocaleId = 'en';
  private bubbleObserver: MutationObserver | null = null;
  /** Watches Omilia shadow DOM for End Chat confirmation buttons. */
  private endChatObserver: MutationObserver | null = null;
  /** Prevents duplicate capture listeners on the same shadow root. */
  private readonly endChatListenerRoots = new WeakSet<ShadowRoot>();
  /** Polls until `window.ChatBot.close` can be hooked after async SDK init. */
  private chatBotRehookTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * True after `close` exists but cannot be replaced (non-configurable CDN shape).
   * Stops rehook polling and limits the console warning to once per mount.
   */
  private closeHookUnavailable = false;
  /** Polls panel visibility so minimize / async End Chat restores our FAB. */
  private panelWatchTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while destroyAndRecreate is tearing down / reopening — ignore close-hook side effects. */
  private remounting = false;
  /**
   * After locale remount, Omilia may still call `window.ChatBot.close()` asynchronously
   * against the *new* global. Swallow those so they do not tear down the reopened panel.
   */
  private suppressCloseUntil = 0;
  /**
   * True while the user has an active Omilia session (open or minimized).
   * Cleared only on End Chat / programmatic close — not on minimize.
   */
  private chatSessionLive = false;
  /** Skips the configurator.close wrap when we already call markClosed ourselves. */
  private suppressConfiguratorCloseHook = false;

  /** True while the native widget session is open (FAB hidden). */
  readonly isOpen = this.openSignal.asReadonly();

  /** True while an Omilia session is active (open or minimized). */
  isSessionActive(): boolean {
    return this.chatSessionLive;
  }

  /**
   * Updates the locale global without remounting the widget.
   * Used when the session has ended and only the app language changed.
   * @param locale New UI locale.
   */
  applyLocaleOnly(locale: LocaleId): void {
    this.activeLocale = locale;
    window.__PuroChatLocale = locale;
  }

  /**
   * Opens the native widget for the given locale.
   * If the session is still mounted but minimized/hidden, expands it instead of remounting.
   * @param locale Active UI locale.
   */
  openChat(locale: LocaleId): void {
    const now = Date.now();
    if (now < this.throttleUntil) {
      return;
    }

    // Minimized / panel-hidden: our FAB is visible (isOpen false) but Omilia DOM remains.
    if (this.configurator && this.hasWidgetDom() && !this.isChatPanelVisible()) {
      this.throttleUntil = now + 1000;
      this.openSignal.set(true);
      const generation = this.openGeneration;
      void this.ensurePanelOpen(this.configurator, generation).then(() => {
        if (generation !== this.openGeneration) {
          return;
        }
        this.ensureChatBotCloseHooked();
        this.startSuppressingOmiliaBubble();
        this.startPanelVisibilityWatch();
        this.startEndChatListener();
        this.openSignal.set(true);
        this.chatSessionLive = true;
      });
      return;
    }

    if (this.openSignal() && this.activeLocale === locale && this.hasWidgetDom()) {
      return;
    }

    this.throttleUntil = now + 1000;
    void this.openNative(locale);
  }

  /**
   * Programmatic close (skips the widget confirmation dialog).
   * Prefer the widget header X so users get the native End Chat confirmation.
   */
  closeChat(): void {
    this.suppressCloseUntil = 0;
    this.remounting = false;
    this.suppressConfiguratorCloseHook = true;
    try {
      this.configurator?.close('both', true);
    } catch {
      // Widget may already be torn down
    } finally {
      this.suppressConfiguratorCloseHook = false;
    }
    this.markClosed();
  }

  /**
   * Rebuilds the widget when the app locale changes and a chat session is active.
   * Omilia binds dialog language/API key at init — setData alone is not enough.
   * @param locale New UI locale.
   */
  async destroyAndRecreate(locale: LocaleId): Promise<void> {
    this.activeLocale = locale;

    if (!this.chatSessionLive || !this.shouldRemountForLocaleChange()) {
      if (this.hasWidgetDom() || this.configurator) {
        this.resetWidget({ invokeSdkClose: false });
      }
      window.__PuroChatLocale = locale;
      return;
    }

    window.__PuroChatLocale = locale;

    this.remounting = true;
    this.openGeneration += 1;
    // Hard teardown only — calling configurator.close() schedules async ChatBot.close on the
    // global, which lands on the next widget after remount and closes it again.
    this.resetWidget({ invokeSdkClose: false });

    try {
      this.throttleUntil = 0;
      // Keep FAB hidden for the whole remount; openNative also sets this.
      this.openSignal.set(true);
      await this.openNative(locale);
      // Stale SDK close callbacks often fire shortly after a successful remount.
      this.suppressCloseUntil = Date.now() + 2_500;
      this.armPostRemountPanelGuard(this.openGeneration);
    } finally {
      this.remounting = false;
    }
  }

  /**
   * Initializes (if needed) and opens the configurator for `locale`.
   */
  private async openNative(locale: LocaleId): Promise<void> {
    const generation = ++this.openGeneration;
    window.__PuroChatLocale = locale;
    this.activeLocale = locale;

    this.openSignal.set(true);
    this.bubbleObserver?.disconnect();
    this.bubbleObserver = null;

    try {
      const configurator = await this.ensureConfigurator(locale);
      if (generation !== this.openGeneration) {
        return;
      }

      // Locale is already applied at init via defaultLocale / findLocale — setData only when needed.
      // Omilia's setData polls ChatBot.setData up to ~5s when the SDK method is missing.
      if (configurator.locale !== locale) {
        await configurator.setData({ locale });
      }
      if (generation !== this.openGeneration) {
        return;
      }

      await this.ensurePanelOpen(configurator, generation);
      if (generation !== this.openGeneration) {
        return;
      }

      this.ensureChatBotCloseHooked();
      this.startSuppressingOmiliaBubble();
      this.startPanelVisibilityWatch();
      this.startEndChatListener();
      this.openSignal.set(true);
      this.chatSessionLive = true;
    } catch (error) {
      console.error('Chatbot failed to open:', error);
      if (generation === this.openGeneration) {
        if (this.isChatPanelVisible()) {
          this.ensureChatBotCloseHooked();
          this.startSuppressingOmiliaBubble();
          this.startPanelVisibilityWatch();
          this.startEndChatListener();
          this.openSignal.set(true);
          this.chatSessionLive = true;
        } else {
          // Allow markClosed even if a remount suppress window was armed earlier.
          this.suppressCloseUntil = 0;
          this.markClosed();
        }
      }
    }
  }

  /**
   * Retries open until the chat panel is visible.
   * Prefer clicking Omilia's launcher bubble (same as WebChatConfigurator.open) — that is what
   * restores a minimized session. ChatBot.open/maximize alone are not always enough.
   * Fall back to ChatBot API / configurator.open when the bubble is missing.
   */
  private async ensurePanelOpen(
    configurator: WebChatConfiguratorInstance,
    generation: number,
  ): Promise<void> {
    const deadline = Date.now() + PANEL_OPEN_TIMEOUT_MS;

    while (Date.now() < deadline) {
      if (generation !== this.openGeneration) {
        return;
      }
      if (this.isChatPanelVisible()) {
        return;
      }

      try {
        if (!this.clickOmiliaLauncherBubble()) {
          if (typeof window.ChatBot?.open === 'function') {
            window.ChatBot.open();
            window.ChatBot.maximize?.();
          } else {
            await configurator.open();
          }
        }
      } catch {
        // Bubble / SDK may not be ready yet on first remount
      }

      if (this.isChatPanelVisible()) {
        return;
      }
      await delay(PANEL_OPEN_RETRY_MS);
    }

    throw new Error('Chat panel did not become visible after open');
  }

  /**
   * Clicks Omilia's launcher bubble even when we have visually hidden it.
   * Used to expand a minimized chat (Omilia's restore path is bubble click).
   * @returns true when a bubble button was found and clicked.
   */
  private clickOmiliaLauncherBubble(): boolean {
    const roots: (ParentNode | null | undefined)[] = [
      document,
      ...Array.from(document.querySelectorAll('.shadow-dom')).map(
        (host) => (host as HTMLElement).shadowRoot,
      ),
    ];

    for (const root of roots) {
      if (!root) {
        continue;
      }
      const bubble = root.querySelector(
        'button[data-test="icon-chat"], button[aria-label="Open Chat"]',
      ) as HTMLElement | null;
      if (!bubble) {
        continue;
      }
      bubble.click();
      return true;
    }

    return false;
  }

  /**
   * Lazily constructs and inits one configurator per locale session.
   */
  private ensureConfigurator(locale: LocaleId): Promise<WebChatConfiguratorInstance> {
    if (this.configurator && this.activeLocale === locale) {
      return Promise.resolve(this.configurator);
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.activeLocale = locale;
    window.__PuroChatLocale = locale;

    this.initPromise = (async () => {
      const Ctor = await this.loader.load(environment.chatbot.configuratorUrl);
      patchFindLocale(Ctor);

      const mobile = isMobileUserAgent(navigator.userAgent);
      const instance = new Ctor({
        debug: false,
        displayMode: mobile ? 'popup' : 'inline',
        inlineWidth: '470px',
        inlineHeight: '650px',
        bypassRecaptcha: true,
        autoOpen: false,
        showCloseConfirmation: true,
        defaultLocale: locale,
        hideControls: {
          minimize: false,
          close: false,
        },
        options: {
          headerLogo: HEADER_LOGO,
          logoUrl: HEADER_LOGO,
          logoAlt: 'Purolator_logo',
          bubbleUrl: BUBBLE_URL,
          title: locale === 'fr' ? 'Assistant virtuel Puro' : 'Purolator Agent',
          brandColor: '#001996',
          headerBgColor: '#001996',
          crossTabSync: false,
          dataPersistent: false,
          notifications: {
            withBubble: false,
            withNumber: false,
            withPulseEffect: false,
          },
          enableTranscriptDownload: false,
        },
        defaultData: { locale },
      });

      instance.findLocale = () => window.__PuroChatLocale ?? locale;

      this.wrapConfiguratorClose(instance);

      await instance.init();
      await waitForChatBotSdk(SDK_READY_TIMEOUT_MS);

      instance.locale = locale;
      this.configurator = instance;
      return instance;
    })().catch((error) => {
      this.initPromise = null;
      throw error;
    });

    return this.initPromise;
  }

  /**
   * Wraps configurator.close so confirmed End Chat (skipConfirmation) schedules markClosed
   * even when the Omilia CDN never calls window.ChatBot.close().
   */
  private wrapConfiguratorClose(instance: WebChatConfiguratorInstance): void {
    const originalClose = instance.close.bind(instance);
    instance.close = (target?: string, skipConfirmation?: boolean): void => {
      originalClose(target, skipConfirmation);
      if (skipConfirmation === true && !this.remounting && !this.suppressConfiguratorCloseHook) {
        this.zone.run(() => {
          this.scheduleMarkClosedWhenPanelHidden(this.openGeneration);
        });
      }
    };
  }

  /**
   * Keeps trying to hook window.ChatBot.close until the SDK global is ready
   * (Omilia may replace the object after configurator.init()).
   */
  private ensureChatBotCloseHooked(): void {
    this.hookWidgetClose();
    this.stopChatBotRehookPoll();

    const closeHookSettled = (): boolean =>
      !!window.ChatBot?.__puroCloseHooked || this.closeHookUnavailable;

    const deadline = Date.now() + SDK_READY_TIMEOUT_MS;
    const tick = (): void => {
      this.hookWidgetClose();
      if (closeHookSettled() || Date.now() >= deadline) {
        this.stopChatBotRehookPoll();
        return;
      }
      this.chatBotRehookTimer = setTimeout(tick, 100);
    };

    if (!closeHookSettled()) {
      this.chatBotRehookTimer = setTimeout(tick, 100);
    }
  }

  private stopChatBotRehookPoll(): void {
    if (this.chatBotRehookTimer !== null) {
      clearTimeout(this.chatBotRehookTimer);
      this.chatBotRehookTimer = null;
    }
  }

  /**
   * Captures clicks on Omilia's End Chat / Terminer confirmation button in shadow DOM.
   */
  private startEndChatListener(): void {
    this.stopEndChatListener();
    this.attachEndChatListenersInDom();
    this.endChatObserver = new MutationObserver(() => this.attachEndChatListenersInDom());
    this.endChatObserver.observe(document.body, { childList: true, subtree: true });
  }

  private stopEndChatListener(): void {
    this.endChatObserver?.disconnect();
    this.endChatObserver = null;
  }

  private attachEndChatListenersInDom(): void {
    document.querySelectorAll('.shadow-dom').forEach((host) => {
      const shadow = (host as HTMLElement).shadowRoot;
      if (!shadow || this.endChatListenerRoots.has(shadow)) {
        return;
      }
      this.endChatListenerRoots.add(shadow);
      shadow.addEventListener(
        'click',
        (event) => {
          const button = (event.target as HTMLElement | null)?.closest('button');
          if (!button || !END_CHAT_BUTTON_PATTERN.test(button.textContent ?? '')) {
            return;
          }
          const generationAtClick = this.openGeneration;
          this.zone.run(() => {
            setTimeout(() => {
              if (generationAtClick !== this.openGeneration) {
                return;
              }
              this.scheduleMarkClosedWhenPanelHidden(generationAtClick);
            }, END_CHAT_MARK_CLOSED_DELAY_MS);
          });
        },
        true,
      );
    });
  }

  /**
   * True when a locale change should remount the widget (open panel or minimized session).
   */
  private shouldRemountForLocaleChange(): boolean {
    if (this.openSignal()) {
      return true;
    }
    return !!(this.configurator && this.hasWidgetDom() && !this.isChatPanelVisible());
  }

  /**
   * After the user confirms End Chat, Omilia calls `window.ChatBot.close()`.
   * Hook that so our FAB returns once the panel is gone.
   * Omilia exposes `close` as a getter-only property — assignment throws; redefine instead.
   * When close is present but non-replaceable, stop retrying (End Chat listener still restores FAB).
   */
  private hookWidgetClose(): void {
    const chatBot = window.ChatBot;
    if (
      !chatBot ||
      typeof chatBot.close !== 'function' ||
      chatBot.__puroCloseHooked ||
      this.closeHookUnavailable
    ) {
      return;
    }

    const original = chatBot.close.bind(chatBot);
    const wrapped = (...args: unknown[]): unknown => {
      // Remount teardown (and its delayed callbacks) must not close the new session.
      if (this.remounting || Date.now() < this.suppressCloseUntil) {
        return undefined;
      }

      const generationAtClose = this.openGeneration;
      const result = original(...args);
      this.zone.run(() => {
        this.scheduleMarkClosedWhenPanelHidden(generationAtClose);
      });
      return result;
    };

    if (!replaceChatBotMethod(chatBot, 'close', wrapped)) {
      this.closeHookUnavailable = true;
      console.warn(
        'ChatBot.close could not be hooked; relying on End Chat listener for FAB restore',
      );
      return;
    }
    chatBot.__puroCloseHooked = true;
  }

  /**
   * Re-opens the panel if a deferred SDK close hides it right after locale remount.
   */
  private armPostRemountPanelGuard(generation: number): void {
    const deadline = Date.now() + 2_500;
    const tick = (): void => {
      if (generation !== this.openGeneration || !this.openSignal()) {
        return;
      }
      if (Date.now() > deadline) {
        return;
      }
      if (!this.isChatPanelVisible() && this.configurator) {
        void this.configurator.open().catch(() => undefined);
        window.ChatBot?.open?.();
      }
      setTimeout(tick, 200);
    };
    setTimeout(tick, 100);
  }

  /**
   * Polls until the panel is hidden after ChatBot.close (Omilia may animate), then restores FAB.
   */
  private scheduleMarkClosedWhenPanelHidden(generationAtClose: number): void {
    const deadline = Date.now() + 2_000;
    const tick = (): void => {
      if (this.remounting || Date.now() < this.suppressCloseUntil) {
        setTimeout(tick, 50);
        return;
      }
      if (generationAtClose !== this.openGeneration) {
        return;
      }
      if (!this.isChatPanelVisible()) {
        this.markClosed();
        return;
      }
      if (Date.now() >= deadline) {
        // Minimize does not call ChatBot.close — this hook only runs after End Chat.
        this.markClosed();
        return;
      }
      setTimeout(tick, 50);
    };
    setTimeout(tick, 50);
  }

  /**
   * Shows our FAB again when the Omilia panel is minimized or otherwise hidden without
   * going through ChatBot.close (we also hide Omilia's own launcher bubble).
   */
  private startPanelVisibilityWatch(): void {
    this.stopPanelVisibilityWatch();
    const tick = (): void => {
      if (this.remounting || Date.now() < this.suppressCloseUntil) {
        this.panelWatchTimer = setTimeout(tick, 100);
        return;
      }
      if (!this.openSignal()) {
        this.panelWatchTimer = null;
        return;
      }
      if (!this.isChatPanelVisible()) {
        // Reveal FAB only — keep configurator/session so the next FAB click can expand.
        this.zone.run(() => this.openSignal.set(false));
        this.panelWatchTimer = null;
        return;
      }
      this.panelWatchTimer = setTimeout(tick, 100);
    };
    this.panelWatchTimer = setTimeout(tick, 100);
  }

  private stopPanelVisibilityWatch(): void {
    if (this.panelWatchTimer !== null) {
      clearTimeout(this.panelWatchTimer);
      this.panelWatchTimer = null;
    }
  }

  /** Restores the FAB, clears Omilia session keys, and tears down widget DOM after End Chat. */
  private markClosed(): void {
    if (Date.now() < this.suppressCloseUntil) {
      return;
    }
    this.chatSessionLive = false;
    this.stopPanelVisibilityWatch();
    this.stopEndChatListener();
    this.stopChatBotRehookPoll();
    this.bubbleObserver?.disconnect();
    this.bubbleObserver = null;
    this.clearOmiliaSessionKeys();
    this.openSignal.set(false);
    this.suppressOmiliaBubbleNow();
    // End Chat leaves Omilia DOM/configurator behind; remove them so locale changes do not remount.
    this.resetWidget({ invokeSdkClose: false });
  }

  private hasWidgetDom(): boolean {
    return !!document.getElementById('root-chatbot') || !!document.querySelector('.shadow-dom');
  }

  private isChatPanelVisible(): boolean {
    const host = document.querySelector('.shadow-dom') as HTMLElement | null;
    const chat = host?.shadowRoot?.querySelector('.chat') as HTMLElement | null;
    if (!chat) {
      return false;
    }
    // Inline style is authoritative for Omilia minimize/close before computed style settles.
    if (chat.style.display === 'none' || chat.style.visibility === 'hidden' || chat.hidden) {
      return false;
    }
    const style = window.getComputedStyle(chat);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  /**
   * Omilia injects its own launcher bubble; hide it so only our FAB remains.
   */
  private startSuppressingOmiliaBubble(): void {
    this.suppressOmiliaBubbleNow();
    if (this.bubbleObserver) {
      return;
    }
    this.bubbleObserver = new MutationObserver(() => this.suppressOmiliaBubbleNow());
    this.bubbleObserver.observe(document.body, { childList: true, subtree: true });
  }

  private suppressOmiliaBubbleNow(): void {
    const hideIn = (root: ParentNode | null | undefined): void => {
      if (!root) {
        return;
      }
      root.querySelectorAll('button[data-test="icon-chat"]').forEach((el) => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
        (el as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
        (el as HTMLElement).setAttribute('aria-hidden', 'true');
      });
    };

    hideIn(document);

    document.querySelectorAll('.shadow-dom').forEach((host) => {
      const shadow = (host as HTMLElement).shadowRoot;
      if (!shadow) {
        return;
      }
      if (!shadow.getElementById(HIDE_BUBBLE_STYLE_ID)) {
        const style = document.createElement('style');
        style.id = HIDE_BUBBLE_STYLE_ID;
        style.textContent = `
          button[data-test="icon-chat"] {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;
        shadow.appendChild(style);
      }
      hideIn(shadow);
    });
  }

  /**
   * Tears down SDK DOM/scripts so a new locale can init cleanly.
   * @param options.invokeSdkClose When false (locale remount), skip configurator.close so the
   *   SDK cannot schedule a deferred `window.ChatBot.close()` onto the next widget instance.
   */
  private resetWidget(options: { invokeSdkClose?: boolean } = {}): void {
    const invokeSdkClose = options.invokeSdkClose !== false;
    this.closeHookUnavailable = false;

    // Unhook before teardown so any remaining SDK close cannot schedule markClosed.
    const chatBot = window.ChatBot;
    if (chatBot?.__puroCloseHooked) {
      chatBot.__puroCloseHooked = false;
      replaceChatBotMethod(chatBot, 'close', () => undefined);
    }

    if (invokeSdkClose) {
      this.suppressConfiguratorCloseHook = true;
      try {
        this.configurator?.close('both', true);
      } catch {
        // ignore
      } finally {
        this.suppressConfiguratorCloseHook = false;
      }
    }

    this.bubbleObserver?.disconnect();
    this.bubbleObserver = null;
    this.stopEndChatListener();
    this.stopChatBotRehookPoll();
    this.stopPanelVisibilityWatch();
    this.initPromise = null;
    this.configurator = null;

    this.clearOmiliaSessionKeys();

    document.getElementById('root-chatbot')?.remove();
    document.querySelectorAll('.shadow-dom').forEach((el) => el.remove());
    document.querySelectorAll('button[data-test="icon-chat"]').forEach((el) => el.remove());
    this.removeOmiliaScripts();

    try {
      delete window.ChatBot;
    } catch {
      window.ChatBot = undefined;
    }

    try {
      delete window.__PuroWebChatConfigurator;
    } catch {
      window.__PuroWebChatConfigurator = undefined;
    }

    // During locale remount keep FAB hidden; destroyAndRecreate / openNative own the signal.
    if (!this.remounting) {
      this.openSignal.set(false);
    }
  }

  private clearOmiliaSessionKeys(): void {
    try {
      sessionStorage.removeItem(OMILIA_SESSION_ACTIVE_KEY);
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && /omilia/i.test(key)) {
          keys.push(key);
        }
      }
      keys.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // ignore
    }
  }

  /**
   * Removes Omilia CDN scripts so the next open can reload configurator + widget bundles.
   */
  private removeOmiliaScripts(): void {
    document.querySelectorAll('script').forEach((el) => {
      const src = el.getAttribute('src') ?? '';
      const inline = el.textContent ?? '';
      const isOmiliaBundle =
        src.includes('ocp.ai') ||
        inline.includes('WebChatConfigurator') ||
        inline.includes('puro-webchat-configurator-ready');
      if (isOmiliaBundle) {
        el.remove();
      }
    });
  }
}

/**
 * Replaces a ChatBot SDK method that may be a getter-only accessor (Omilia CDN).
 * @returns true when the replacement was installed.
 */
function replaceChatBotMethod(
  chatBot: NonNullable<Window['ChatBot']>,
  key: 'close' | 'open',
  replacement: (...args: unknown[]) => unknown,
): boolean {
  try {
    (chatBot as unknown as Record<string, unknown>)[key] = replacement;
    if (chatBot[key] === replacement) {
      return true;
    }
  } catch {
    // Getter-only accessors throw on assignment in some engines
  }

  try {
    Object.defineProperty(chatBot, key, {
      configurable: true,
      enumerable: true,
      writable: true,
      value: replacement,
    });
    // Do not require reference equality — some hosts wrap the installed function.
    return typeof chatBot[key] === 'function';
  } catch {
    return false;
  }
}

/**
 * Omilia's findLocale() only checks whether the URL path contains `fr`.
 * Patch so the app locale wins on shipping app routes.
 */
function patchFindLocale(Ctor: WebChatConfiguratorConstructor): void {
  const proto = Ctor.prototype as WebChatConfiguratorInstance;
  proto.findLocale = function patchedFindLocale(): string {
    return window.__PuroChatLocale ?? 'en';
  };
}

/**
 * Waits until window.ChatBot is available after the Omilia bundle script loads.
 */
function waitForChatBotSdk(timeoutMs: number): Promise<void> {
  const started = Date.now();
  return new Promise((resolve, reject) => {
    const tick = (): void => {
      if (window.ChatBot && typeof window.ChatBot.open === 'function') {
        resolve();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('ChatBot SDK did not become ready in time'));
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });
}

/**
 * Promise-based delay used between open retries.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
